import { inject, Injectable } from '@angular/core';

import { Dashboard, DashboardService } from '../providers/bridge-api/dashboard.service';
import { RealtimeMessage } from '../providers/realtime.service';
import { AiInsightsService } from '../providers/semantria-api/ai-insights.service';
import {
  AnalysisGroup,
  AnalysisGroupService,
} from '../providers/storage-api/analysis-group-service';
import { Analysis, AnalysisService } from '../providers/storage-api/analysis.service';
import { Collection, CollectionService } from '../providers/storage-api/collection.service';
import {
  CalculationListItem,
  PredictiveService,
} from '../providers/storage-api/predictive.service';
import { Upload, UploadService } from '../providers/storage-api/upload.service';
import { EnableClassTelemetry } from './otel.service';

@EnableClassTelemetry()
@Injectable({
  providedIn: 'root',
})
export class RtrEventHandlerService {
  analysisService = inject(AnalysisService);
  analysisGroupService = inject(AnalysisGroupService);
  collectionService = inject(CollectionService);
  dashboardService = inject(DashboardService);
  uploadService = inject(UploadService);
  predictiveService = inject(PredictiveService);
  aiInsightService = inject(AiInsightsService);

  handleAnalysisRtrEvent(
    event: RealtimeMessage<{ id: string; collectionId: string; projectId: string }>
  ): Promise<Analysis> {
    const { id, collectionId, projectId } = event.object;
    return this.analysisService.getAnalysisByName(collectionId, id, projectId, 'objectLabels');
  }

  handleAnalysisGroupRtrEvent(
    event: RealtimeMessage<{ id: string; projectId: string }>
  ): Promise<AnalysisGroup> {
    const { id, projectId } = event.object;
    const silent = ((window as any)?.appConfig?.rtrelay?.suppressErrors ?? true) === true;
    return this.analysisGroupService.getAnalysisGroup(projectId, id, { silent }, 'objectLabels');
  }

  handleCollectionRtrEvent(
    event: RealtimeMessage<{ id: string; projectId: string }>
  ): Promise<Collection> {
    const { id, projectId } = event.object;
    return this.collectionService.getCollectionByName(id, projectId, false, 'objectLabels');
  }

  handleDashboardRtrEvent(
    event: RealtimeMessage<{ id: string; projectId: string }>
  ): Promise<Dashboard> {
    const { id, projectId } = event.object;
    return this.dashboardService.getDashboardById(id, projectId, 'objectLabels');
  }

  async handleDriverAnalysisRtrEvent(
    event: RealtimeMessage<{ id: string; projectId: string; collectionId: string }>
  ): Promise<CalculationListItem> {
    const { id, collectionId, projectId } = event.object;
    const calcs = await this.predictiveService.listCalculations(projectId, collectionId);
    if (calcs) {
      const calculation = calcs.items.find((calc) => calc.id === id);
      if (calculation) {
        return new Promise((res) => res(calculation));
      }
    }
  }

  handleUploadRtrEvent(
    event: RealtimeMessage<{ id: string; collectionId: string; projectId: string }>
  ): Promise<Upload> {
    const { id, projectId } = event.object;
    return this.uploadService.getUploadById(id, projectId);
  }

  handleAiInsightRtrEvent(event: RealtimeMessage<{ id: string }>) {
    return this.aiInsightService.getInsight(event.object.id);
  }
}
